import { useCallback, useState } from "react";
import { useApi } from "../api";

const getLockCalculatorReportUrl = (calculationId: string): string => `/modules/lock_calculator/${calculationId}/report`;

/** Le catture del viewer 3D (vedi `OrthographicViews` in 3d/LockSceneCapture.tsx).*/
type Views3D = {
  perspective: string;
  front: string;
  top: string;
  sideRight: string;
  sideLeft: string;
  exploded: string;
};

const stripDataPrefix = (dataUrl: string): string => {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
};

const DEFAULT_REPORT_MODULE_NAME = "lock-t";

const sanitizeFileNamePart = (value: string | null | undefined): string =>
  value?.trim().replace(/[^a-z0-9-_]+/gi, "-").replace(/^-+|-+$/g, "") ?? "";

/** Builds "<module>-<yyyy-mm-dd>-<calculation>[-<project>].pdf", sanitized, matching the deckDesigner download naming convention. */
export const getLockCalculatorReportFileName = (
  moduleName: string | null | undefined,
  calculationName: string | null | undefined,
  projectName: string | null | undefined,
): string => {
  const creationDate = new Date().toISOString().slice(0, 10);
  const parts = [
    sanitizeFileNamePart(moduleName) || DEFAULT_REPORT_MODULE_NAME,
    creationDate,
    sanitizeFileNamePart(calculationName),
    sanitizeFileNamePart(projectName),
  ].filter(Boolean);
  return `${parts.join("-")}.pdf`;
};

/** Fetches and downloads/previews the lock_calculator PDF report. */
export const useLockCalculatorReportApi = () => {
  const api = useApi();
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const buildReportBody = (views: Views3D, language: string) => ({
    perspective_png_b64: stripDataPrefix(views.perspective),
    front_png_b64: stripDataPrefix(views.front),
    top_png_b64: stripDataPrefix(views.top),
    side_right_png_b64: stripDataPrefix(views.sideRight),
    side_left_png_b64: stripDataPrefix(views.sideLeft),
    exploded_png_b64: stripDataPrefix(views.exploded),
    language,
  });

  /** Fetches the report as a blob object URL, for inline preview only. */
  const getLockCalculatorReport = useCallback(
    async (calculationId: string, views: Views3D, language: string): Promise<string> => {
      setIsLoadingReport(true);
      try {
        return await api.postForFileObjectUrl(getLockCalculatorReportUrl(calculationId), buildReportBody(views, language));
      } finally {
        setIsLoadingReport(false);
      }
    },
    [api],
  );

  /** Fetches the report and immediately saves it client-side under a meaningful filename. */
  const downloadLockCalculatorReport = useCallback(
    async (calculationId: string, views: Views3D, language: string, fileName: string): Promise<void> => {
      setIsLoadingReport(true);
      try {
        await api.postForFileDownload(getLockCalculatorReportUrl(calculationId), buildReportBody(views, language), fileName);
      } finally {
        setIsLoadingReport(false);
      }
    },
    [api],
  );

  /** Downloads every draft configuration as CSV (built server-side, not capped like the loaded drafts). */
  const downloadConfigurationsCsv = useCallback(
    (calculationId: string, language: string, fileName: string): Promise<void> =>
      api.postForFileDownload(`/modules/lock_calculator/${calculationId}/configurations/export`, { language }, fileName),
    [api],
  );

  return {
    isLoadingReport,
    getLockCalculatorReport,
    downloadLockCalculatorReport,
    downloadConfigurationsCsv,
  };
};

export default useLockCalculatorReportApi;
